import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { requestPasswordReset } from '../api/api';
import { ArrowLeft, Mail, Send } from 'lucide-react';

const Logo = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 7.5L12 4L20 7.5L12 11L4 7.5Z" fill="currentColor" />
    <path d="M4 11.5L12 15L20 11.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 15.5L12 19L20 15.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      await requestPasswordReset(email);
      setSuccess('Se o email estiver cadastrado, você receberá um link para redefinir sua senha.');
    } catch (error) {
      setError('Erro ao solicitar recuperação de senha. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8] flex flex-col items-center justify-center p-4 bg-grid">
      <div className="absolute top-8 left-8">
        <Link 
          to="/login"
          className="flex items-center gap-2 text-gray-500 hover:text-purple-600 font-bold transition-all group"
        >
          <div className="w-10 h-10 rounded-full bg-white border border-gray-100 flex items-center justify-center group-hover:bg-purple-50 group-hover:border-purple-100 shadow-sm transition-all">
            <ArrowLeft size={18} />
          </div>
          <span>Voltar ao login</span>
        </Link>
      </div>

      <div className="w-full max-w-md animate-fade-in">
        <div className="flex flex-col items-center text-center mb-10">
          <div className="flex items-center text-black mb-4">
            <Logo size={48} />
            <span className="font-display text-4xl ml-4 tracking-tighter">SORA</span>
          </div>
          <h1 className="text-3xl font-black mb-2 font-hero">
            Recuperar Senha
          </h1>
          <p className="text-gray-500 font-medium">
            Digite seu email e enviaremos um link para redefinir sua senha.
          </p>
        </div>

        <div className="bg-white p-8 md:p-10 rounded-[3rem] shadow-xl border border-gray-100">
          {success && (
            <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-r-xl">
              <p className="text-green-800 font-medium text-sm">{success}</p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-xl">
              <p className="text-red-800 font-medium text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">
                Seu e-mail
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                  <Mail size={18} />
                </div>
                <input 
                  type="email" 
                  placeholder="exemplo@escola.com" 
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  className="w-full bg-gray-50 border-2 border-gray-100 focus:border-purple-200 rounded-2xl py-4 pl-12 pr-4 outline-none transition-all font-medium disabled:opacity-50"
                />
              </div>
            </div>

            <button 
              type="submit"
              disabled={isLoading}
              className={`w-full bg-black text-white font-display py-5 rounded-2xl text-lg shadow-xl shadow-gray-200 transition-all active:scale-95 flex items-center justify-center gap-3 ${isLoading ? 'opacity-70 cursor-wait' : 'hover:bg-gray-800'}`}
            >
              {isLoading ? (
                <div className="w-6 h-6 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  <Send size={20} />
                  Enviar Link de Recuperação
                </>
              )}
            </button>
          </form>

          <div className="mt-8 pt-8 border-t border-gray-50 text-center">
            <p className="text-gray-500 font-medium">
              Lembrou sua senha? <br className="sm:hidden" />
              <Link 
                to="/login" 
                className="text-purple-600 font-bold hover:underline ml-1"
              >
                Voltar para o login
              </Link>
            </p>
          </div>
        </div>
      </div>
      
      <p className="mt-12 text-gray-400 text-sm font-medium">
        © 2025 Sôra — Todos os direitos reservados.
      </p>
    </div>
  );
};

export default ForgotPassword;
